import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../App/redux/store';
import { genericUpdateKeys } from './constants';

const updateState =
  <T extends typeof initialState, K extends keyof T>(key: K, validKeys: K[]) =>
  (state: T, action: PayloadAction<T[K]>) => {
    if (validKeys.includes(key)) {
      state[key] = action.payload;
    } else {
      console.error(`Invalid key update: ${key as string}`);
    }
  };

const initialState: ModalsState = {
  subscription: false,
  userMobileTabs: false,
  mobileNav: false,
  errorPiecework: false,
  notificationBell: false,
  profileMenu: false,
  adDetails: null,
  isLateralOpen: false,
  deleteAds: false,
  confirmation: false,
  subscriptionSummary: null,
  createOrUpdateUser: { open: false, entity: null },
  noUrbaneaseAccount: null,
  chooseFolder: null,
  deleteFolder: null,
  updateFolder: null,
  deleteComparativeStudy: null,
  comparativeStudy: false,
  deleteMarketStudy: null,
};

const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    reset: () => initialState,

    userMobileTabs: updateState('userMobileTabs', genericUpdateKeys),
    mobileNav: updateState('mobileNav', genericUpdateKeys),
    errorPiecework: updateState('errorPiecework', genericUpdateKeys),
    notificationBell: updateState('notificationBell', genericUpdateKeys),
    profileMenu: updateState('profileMenu', genericUpdateKeys),
    deleteAds: updateState('deleteAds', genericUpdateKeys),
    confirmation: updateState('confirmation', genericUpdateKeys),
    adDetails: (state, action: PayloadAction<string | null>) => {
      state.adDetails = action.payload;
      state.isLateralOpen = Boolean(action.payload);
    },
    createOrUpdateUser: (
      state,
      action: PayloadAction<ModalWithValueType<IUser | null>>,
    ) => {
      state.createOrUpdateUser = action.payload;
    },
    noUrbaneaseAccount: (state, action: PayloadAction<NoUrbaneaseAccountType>) => {
      state.noUrbaneaseAccount = action.payload;
    },
    chooseFolder: (state, action: PayloadAction<ChooseFolderType>) => {
      state.chooseFolder = action.payload;
    },
    deleteFolder: (state, action: PayloadAction<IFolder | null>) => {
      state.deleteFolder = action.payload;
    },
    updateFolder: (state, action: PayloadAction<IFolder | null>) => {
      state.updateFolder = action.payload;
    },
    deleteComparativeStudy: (
      state,
      action: PayloadAction<DeleteComparativeStudyType>,
    ) => {
      state.deleteComparativeStudy = action.payload;
    },
    comparativeStudy: (state, action: PayloadAction<boolean>) => {
      state.comparativeStudy = action.payload;
      state.isLateralOpen = action.payload;
    },
    askForSubscriptionUpdate: (state, action: PayloadAction<boolean>) => {
      state.subscription = action.payload;
      state.isLateralOpen = action.payload;
    },
    subscriptionUpdateSummary: (
      state,
      action: PayloadAction<SubscriptionUpdateFormType | null>,
    ) => {
      if (state.subscription) state.subscription = false;
      if (state.isLateralOpen) state.isLateralOpen = false;

      state.subscriptionSummary = action.payload;
    },
    deleteMarketStudy: (state, action: PayloadAction<DeleteMarketStudyType>) => {
      state.deleteMarketStudy = action.payload;
    },
  },
});

export default modalsSlice.reducer;
export const modalsActions = modalsSlice.actions;
export const getModalsState = (state: RootState) => state.modals;
